<template>
  <ul class="attendees-list" ref="refRoot" tabindex="0">
    <li v-for="attendee in sortedAttendees" :key="attendee.userId" class="attendee-item">
      <!-- 아이콘 -->
      <IconProfile
        :color-profile="getColorProfile(attendee.userId)"
        :initials="getInitials(attendee.nickname)"
        :is-partner-person="attendee.isPartnerPerson"
      />
      <!-- 나/호스트 -->
      <div v-if="getIsVisibleAttendeeStatus(attendee)" class="attendee-status">
        <p class="small dim accent">{{ getTextIdentifier(attendee) }}</p>
      </div>
      <!-- 닉네임 -->
      <p class="small attendee-info">{{ attendee.nickname }}</p>

      <!-- 강퇴버튼 -->
      <a v-if="!attendee.isPartnerPerson && isPartnerPerson" @click="evict(attendee)" class="link-small">
        {{ $t('alert_kick_out') }}
      </a>
    </li>
  </ul>
</template>

<script setup lang="ts">
import { computed, defineProps, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import IconProfile from '@/components/icons/IconProfile.vue';
import { useProfile } from '@/composables/domains/chat/useProfile';
import { useKeyPress } from '@/composables/shared/useKeyPress';
import { useCallStore } from '@/stores/call';
import { QRCodeType } from '@/stores/chat';
import { useUserStore } from '@/stores/user';
import { Attendee } from '@/types/socket/message';

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    attendees: Attendee[];
  }>(),
  {
    attendees: () => [],
  }
);

const emit = defineEmits(['evict', 'close']);

const callStore = useCallStore();
const userStore = useUserStore();

const refRoot = ref<HTMLElement | null>(null);

const sortedAttendees = computed(() => {
  const myUserId = userStore.userInfo.userId;
  return [...props.attendees].sort((a, b) => {
    if (a.isPartnerPerson && b.isPartnerPerson) return -1;
    if (b.isPartnerPerson && a.isPartnerPerson) return 1;
    if (a.userId === myUserId && b.userId !== myUserId) return -1;
    if (b.userId === myUserId && a.userId !== myUserId) return 1;
    return 0;
  });
});

const isPartnerPerson = computed(() => {
  return QRCodeType.isEmployee(callStore.qrCodeInfo.qrCodeType);
});

const evict = (attendee) => {
  emit('evict', attendee);
};

const getTextIdentifier = (attendee) => {
  const isMe = userStore.userInfo.userId === attendee.userId;
  const isPartner = attendee.isPartnerPerson === '1';
  if (isMe) return t('me');
  if (isPartner) return t('chat_host');
};

const getIsVisibleAttendeeStatus = (attendee) => {
  const isMe = userStore.userInfo.userId === attendee.userId;
  const isPartner = attendee.isPartnerPerson;
  return isMe || isPartner;
};

const { getColorProfile, getInitials } = useProfile();

useKeyPress(refRoot, {
  Escape: () => {
    emit('close');
  },
});

onMounted(() => {
  refRoot.value?.focus();
});
</script>

<style scoped lang="scss">
.attendees-list {
  width: 100%;
  padding: 0;
  margin: 0.25rem 0 0 0;
  list-style: none;
}

.attendee-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.attendee-item:not(:last-child) {
  margin-bottom: 0.75rem;
}

ul {
  width: 100%;
}

.attendee-status {
  width: fit-content;
  display: flex;
  white-space: nowrap;
}

.attendee-info {
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
